import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { User, ExternalLink, Mail } from 'lucide-react';
import { usersAPI } from '../services/api';
import { User as UserType } from '../types/guide';
import { getUserDisplayName } from '../utils/userUtils';

const UserBioPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!userId) {
        setError('User ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userData = await usersAPI.getUserById(userId);
        setUser(userData.user);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching user profile:', err);
        setError(err.response?.data?.error || 'Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [userId]);

  const getDisplayName = () => {
    if (!user) return 'User';
    
    return getUserDisplayName({
      displayName: user.displayName,
      firstName: user.firstName,
      lastName: user.lastName
    });
  };

  const getSocialMediaIcon = (platform: string) => {
    const lowerPlatform = platform.toLowerCase();
    
    if (lowerPlatform.includes('twitter') || lowerPlatform.includes('x.com')) {
      return '𝕏';
    } else if (lowerPlatform.includes('facebook')) {
      return '📘';
    } else if (lowerPlatform.includes('instagram')) {
      return '📷';
    } else if (lowerPlatform.includes('linkedin')) {
      return '💼';
    } else if (lowerPlatform.includes('youtube')) {
      return '📺';
    } else if (lowerPlatform.includes('tiktok')) {
      return '🎵';
    } else if (lowerPlatform.includes('github')) {
      return '🐙';
    } else {
      return '🔗';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-500">Loading user profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h1 className="text-xl font-semibold text-red-800 mb-2">Profile Not Found</h1>
            <p className="text-red-600 mb-4">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-8 sm:px-8">
            <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
              {/* Profile Photo */}
              <div className="flex-shrink-0">
                {user?.profileImageUrl ? (
                  <img
                    src={user.profileImageUrl}
                    alt={`${getDisplayName()}'s profile`}
                    className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover"
                    crossOrigin="anonymous"
                    referrerPolicy="strict-origin-when-cross-origin"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`w-24 h-24 rounded-full border-4 border-white shadow-lg bg-white dark:bg-gray-800 flex items-center justify-center ${user?.profileImageUrl ? 'hidden' : ''}`}>
                  <User className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1 text-center sm:text-left">
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  {getDisplayName()}
                </h1>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="px-6 py-8 sm:px-8">
            {/* Bio Section */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">About</h2>
              {user?.bio ? (
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {user.bio}
                  </p>
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 dark:text-gray-500 italic">No bio available.</p>
              )}
            </div>

            {/* Social Media Links */}
            {user?.socialMediaLinks && user.socialMediaLinks.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Connect</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {user.socialMediaLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:bg-primary-900/20 transition-colors group"
                    >
                      <span className="text-xl mr-3 group-hover:scale-110 transition-transform">
                        {getSocialMediaIcon(link.platform)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {link.platform}
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-primary-600 transition-colors" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Section */}
            {user?.email && (
              <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Contact</h2>
                <a
                  href={`mailto:${user.email}`}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserBioPage;